import PageHeader from './PageHeader';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Update } from '../types';
import updatesService from '../services/updatesService';
import { categoryLabelFor, categoryStyleFor, formatUpdateDate } from '../utils/updatePresentation';

const UpdatesPage: React.FC = () => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const [updates, setUpdates] = useState<Update[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        let active = true;
        setIsLoading(true);
        updatesService
            .getUpdates()
            .then(data => {
                if (active) setUpdates(data);
            })
            .finally(() => {
                if (active) setIsLoading(false);
            });
        return () => {
            active = false;
        };
    }, [i18n.language]);

    return (
        <div className="min-h-full pb-20">
            <div className="mx-auto max-w-3xl px-5 py-9 sm:px-8 lg:px-10">
                <div className="mb-8 animate-fade-in">
                    <PageHeader title={t('appShell.nav.updates', { defaultValue: 'Updates' })} />
                    <p className="mt-1.5" style={{ color: 'var(--text-muted)' }}>
                        Alles wat er nieuw is en verbeterd in Notizy
                    </p>
                </div>

                {isLoading ? (
                    <div className="flex items-center justify-center py-24">
                        <svg className="h-7 w-7 animate-spin text-primary-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                    </div>
                ) : updates.length === 0 ? (
                    <div className="animate-fade-in rounded-2xl border border-primary-100 bg-container p-12 text-center shadow-soft">
                        <p style={{ color: 'var(--text-muted)' }}>Er zijn nog geen updates.</p>
                    </div>
                ) : (
                    <ol className="relative space-y-4 border-l border-primary-100 pl-6">
                        {updates.map((update, index) => {
                            const category = categoryStyleFor(update.category);
                            return (
                                <li
                                    key={update.id}
                                    className="relative animate-slide-up"
                                    style={{ animationDelay: `${index * 0.05}s` }}
                                >
                                    {/* Tijdlijn-stip */}
                                    <span
                                        className="absolute -left-[31px] top-6 h-3 w-3 rounded-full ring-4"
                                        style={{ background: 'var(--primary-600)', '--tw-ring-color': 'var(--background)' } as React.CSSProperties}
                                        aria-hidden="true"
                                    />
                                    <button
                                        onClick={() => navigate(`/updates/${update.slug}`)}
                                        className="block w-full rounded-2xl border border-primary-100 bg-container p-5 text-left shadow-soft transition-all duration-200 hover:border-[color:var(--hover-outline)] hover:shadow-medium"
                                    >
                                        <div className="flex flex-wrap items-center gap-2.5">
                                            <span
                                                className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold"
                                                style={{ background: category.bg, color: category.color }}
                                            >
                                                {categoryLabelFor(update)}
                                            </span>
                                            <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                                                {formatUpdateDate(update.publishedAt)}
                                            </span>
                                            {update.version && (
                                                <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                                                    · v{update.version}
                                                </span>
                                            )}
                                        </div>
                                        <h2 className="mt-2 font-serif text-xl" style={{ color: 'var(--text-strong)' }}>
                                            {update.title}
                                        </h2>
                                        <p className="mt-1 text-sm leading-relaxed" style={{ color: 'var(--text-muted)' }}>
                                            {update.summary}
                                        </p>
                                        <span className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-primary-700">
                                            Lees meer
                                            <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                                            </svg>
                                        </span>
                                    </button>
                                </li>
                            );
                        })}
                    </ol>
                )}
            </div>
        </div>
    );
};

export default UpdatesPage;
